import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FaFacebook, FaWhatsapp, FaComment, FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { IoIosShareAlt } from "react-icons/io";
import { useAuth } from "../context/AuthContext";
import { ThumbsUp } from "lucide-react";
import { format } from "date-fns";
import "../styles/ArticleDetail.css";
import AdSenseAd from "./AdSenseAd";
import RelatedArticleItem from "./RelatedArticleItem";
import Login from "../pages/Login";
import Signup from "../pages/Signup";
import LoadingSpinner from "./LoadingSpinner";
import "../styles/main.css";

// API Mappings for each category
const CATEGORY_API_MAP = {
  // Updates: "https://jobs.mpdatahub.com/api/newsarticles",
  Education: "https://jobs.mpdatahub.com/api/study-articles",
  Entertainment: "https://jobs.mpdatahub.com/api/movienewsartcles",
  Insights: "https://jobs.mpdatahub.com/api/getScientificFacts",
  Technology: "https://jobs.mpdatahub.com/api/technews",
  Business: "https://jobs.mpdatahub.com/api/newsarticles",
  Health: "https://jobs.mpdatahub.com/api/getHealthArticles",
  Sports: "https://jobs.mpdatahub.com/api/getSportsArticles",
  Education: "https://jobs.mpdatahub.com/api/study-articles",
  Stories: "https://jobs.mpdatahub.com/api/getShortStories",
  Updates: "https://jobs.mpdatahub.com/api/tech-updates"
};

const ArticleDetail = () => {
  const commentSectionRef = useRef(null);
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentName, setCommentName] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState(null);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [authMode, setAuthMode] = useState("choice");
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [loadingLike, setLoadingLike] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { googleLogin, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Get time ago function for latest news sidebar
  const getTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  // Extract category from the article ID or location state
  const getCategoryFromId = (articleId) => {
    if (!articleId) return "Updates";
    if (location.state?.category) {
      return location.state.category;
    }
    const idParts = articleId.split('-');
    if (idParts.length > 1) {
      const possibleCategory = idParts[0].charAt(0).toUpperCase() + idParts[0].slice(1);
      if (CATEGORY_API_MAP[possibleCategory]) {
        return possibleCategory;
      }
    }
    return "Updates";
  };

  // Fetch like status for the article
  useEffect(() => {
    const fetchLikeStatus = async () => {
      if (!article) return;
      try {
        setLiked(false);
        setLikesCount(0);
      } catch (error) {
        console.error("Error fetching like status:", error);
      }
    };

    if (article) {
      fetchLikeStatus();
    }
  }, [article]);

  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem("token");
      return !!token;
    };
    if (checkAuthStatus() && showLoginModal) {
      setShowLoginModal(false);
    }
  }, [currentUser, showLoginModal]);

  const handleLike = async (e) => {
    e.stopPropagation();
    const storedUser = JSON.parse(localStorage.getItem("user"));
    const userId = storedUser?.id;
    if (!userId) {
      setShowLoginModal(true);
      return;
    }
    setLoadingLike(true);
    try {
      const articleId = article.id;
      if (!articleId) {
        throw new Error("Article ID not found");
      }
      const res = await fetch(
        `https://tnreaders.mpdatahub.com/mobile/Content/like`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: userId,
            category: article.category,
            id: articleId
          }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        setLiked(!liked);
        setLikesCount(prev => liked ? prev - 1 : prev + 1);
      } else {
        throw new Error("Failed to like article");
      }
    } catch (error) {
      console.error("Error liking article:", error);
      alert("Failed to like article. Please try again.");
    } finally {
      setLoadingLike(false);
    }
  };

  const toggleShareOptions = (e) => {
    e.stopPropagation();
    setShowShareOptions(!showShareOptions);
  };

  const fetchComments = async () => {
    try {
      setCommentLoading(true);
      const articleId = article?.id || id;
      if (!articleId) return;
      const response = await fetch(
        `https://tnreaders.mpdatahub.com/mobile/comments/${articleId}`
      );
      if (response.ok) {
        const data = await response.json();
        setComments(Array.isArray(data) ? data : []);
      } else {
        setComments([]);
      }
    } catch (err) {
      setCommentError("Failed to load comments");
      console.error("Error fetching comments:", err);
      setComments([]);
    } finally {
      setCommentLoading(false);
    }
  };

  useEffect(() => {
    if (article) {
      fetchComments();
    }
  }, [article]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !article) return;
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) {
      setShowLoginModal(true);
      return;
    }
    const userName = storedUser?.name || storedUser?.displayName || "Anonymous";
    setCommentName(userName);

    try {
      setCommentLoading(true);
      const articleId = article.id;
      const response = await fetch("https://tnreaders.mpdatahub.com/mobile/comments-store", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          article_metadata_id: articleId,
          comment_name: userName,
          comment_text: newComment,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to post comment");
      }
      const savedComment = await response.json();
      const newCommentObj = {
        id: savedComment.id || Date.now(),
        comment_name: userName,
        comment_text: newComment,
        created_at: new Date().toISOString(),
        replies: []
      };
      setComments([...comments, newCommentObj]);
      setNewComment("");
      setCommentError(null);
      setShowCommentForm(false);
      fetchComments();
    } catch (err) {
      setCommentError(err.message);
      alert(err.message);
      console.error("Comment submission error:", err);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleReplySubmit = async (commentId, replyText) => {
    if (!replyText.trim()) return;
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) {
      setShowLoginModal(true);
      return;
    }
    const userName = storedUser?.name || storedUser?.displayName || "Anonymous";
    try {
      const response = await fetch(`https://tnreaders.mpdatahub.com/mobile/comments/${commentId}/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          comment_id: commentId,
          reply_name: userName,
          reply_text: replyText,
        }),
      });
      if (response.ok) {
        const savedReply = await response.json();
        fetchComments();
      }
    } catch (err) {
      console.error("Error posting reply:", err);
      alert("Failed to post reply. Please try again.");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) return;
    try {
      const response = await fetch(`https://tnreaders.mpdatahub.com/mobile/comments/destroy/${commentId}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setComments(comments.filter(comment => comment.id !== commentId));
      }
    } catch (err) {
      console.error("Error deleting comment:", err);
      alert("Failed to delete comment. Please try again.");
    }
  };

  const shareOnSocialMedia = (platform, e) => {
    e.stopPropagation();
    if (!article) return;
    const articleId = article.id;
    const articleUrl = `${window.location.origin}/article/${articleId}`;
    const shareText = `🔷 *${article.title}* 🔷\n\n${article.summary || article.description || ""
      }\n\nRead more: ${articleUrl}`;
    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            articleUrl
          )}`,
          "_blank"
        );
        break;
      case "whatsapp":
        window.open(
          `https://wa.me/?text=${encodeURIComponent(shareText)}`,
          "_blank"
        );
        break;
      default:
        break;
    }
    setShowShareOptions(false);
  };

  useEffect(() => {
    const fetchArticleAndRelated = async () => {
      try {
        setLoading(true);
        setError(null);
        if (location.state?.article) {
          setArticle(location.state.article);
          setLoading(false);
        }
        const category = getCategoryFromId(id);
        if (!category || !CATEGORY_API_MAP[category]) {
          throw new Error(`Invalid category: ${category}`);
        }
        const apiUrl = CATEGORY_API_MAP[category];
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${category} articles`);
        }
        const data = await response.json();
        const articles = Array.isArray(data) ? data : data.data || data.articles || [];
        const formattedArticles = articles.map((item, index) => {
          let tags = [];
          if (Array.isArray(item.tags)) {
            tags = item.tags;
          } else if (typeof item.tags === 'string') {
            tags = item.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
          } else if (item.tags) {
            tags = [String(item.tags)];
          }
          return {
            ...item,
            category: category,
            date: item.date || item.createdAt || item.publishedAt || item.time || new Date().toISOString(),
            id: item.id || item._id || `${category.toLowerCase()}-${index}`,
            title: item.title || item.heading || `Article ${index + 1}`,
            description: item.description || item.summary || item.content || "",
            imageUrl: item.imageUrl || item.image || item.thumbnail || item.img || "/default-news-image.jpg",
            author: item.author || "Admin",
            tags: tags,
            summary: item.summary || item.description?.substring(0, 150) || ""
          };
        });
        setAllArticles(formattedArticles);
        if (!location.state?.article) {
          const foundArticle = formattedArticles.find(
            art => art.id === id || art._id === id
          );
          if (!foundArticle) {
            throw new Error("Article not found");
          }
          setArticle(foundArticle);
        }
      } catch (err) {
        setError(err.message);
        console.error("Fetch error:", err);
        if (!article) {
          setError("Unable to load article. Please try again.");
        }
      } finally {
        setLoading(false);
        setLoadingRelated(false);
      }
    };
    fetchArticleAndRelated();
  }, [id, location.state]);

  const renderArticleContent = () => {
    if (!article) return null;
    const content = article.description || article.content || "";
    if (!content.trim()) {
      return (
        <div className="no-content">
          <p>Content coming soon...</p>
          <p>Check back later for the full article.</p>
        </div>
      );
    }
    const paragraphs = content.split("\n").filter((p) => p.trim());
    const elements = [];
    let paragraphCount = 0;
    let firstStyled = false;
    paragraphs.forEach((paragraph, index) => {
      const isSubheading =
        paragraph.length < 60 &&
        (paragraph.endsWith(":") || !paragraph.includes("."));
      if (isSubheading) {
        elements.push(
          <React.Fragment key={`subheading-${index}`}>
            <h3 className="article-subheading">{paragraph}</h3>
          </React.Fragment>
        );
      } else {
        let contentElement;
        if (!firstStyled && paragraph.length > 0) {
          const words = paragraph.split(" ");
          const firstWord = words[0];
          const firstLetter = firstWord.charAt(0);
          const restOfFirstWord = firstWord.slice(1);
          const restOfParagraph = words.slice(1).join(" ");
          contentElement = (
            <>
              <span className="first-letter">{firstLetter}</span>
              {restOfFirstWord} {restOfParagraph}
            </>
          );
          firstStyled = true;
        } else {
          contentElement = paragraph;
        }
        elements.push(
          <React.Fragment key={`para-${index}`}>
            <p className="article-paragraph">{contentElement}</p>
          </React.Fragment>
        );
        paragraphCount++;
      }
      if (
        paragraphCount > 0 &&
        paragraphCount % 3 === 0 &&
        index < paragraphs.length - 1
      ) {
        elements.push(
          <div key={`ad-${index}`} className="article-ad">
            <AdSenseAd
              slot="3581145953"
              style={{ display: "block", width: "100%", height: "250px" }}
              format="display"
            />
          </div>
        );
      }
    });
    return elements;
  };

  const getRelatedArticles = () => {
    if (!article || allArticles.length === 0) return [];
    const currentArticleId = article.id;
    return allArticles
      .filter(
        (art) => {
          const artId = art.id;
          return artId !== currentArticleId && art.category === article.category;
        }
      )
      .slice(0, 5);
  };

  const latestNews = useMemo(() => {
    if (!allArticles.length) return [];
    const currentArticleId = article?.id;
    return allArticles
      .filter(art => {
        const artId = art.id;
        return artId !== currentArticleId;
      })
      .slice(0, 5);
  }, [allArticles, article]);

  const handleGoogleSuccess = async (credential) => {
    try {
      setError("");
      setLoading(true);
      const result = await googleLogin(credential);
      if (result.success) {
        setTimeout(() => {
          handleLike({ stopPropagation: () => { } });
        }, 500);
      } else {
        setError(result.message || "Google authentication failed");
      }
    } catch (error) {
      setError("Google authentication error");
      console.error("Google auth error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = (errorMessage) => {
    setError(errorMessage || "Google sign-in failed. Please try again.");
  };
  const handleEmailLogin = () => {
    setShowLoginModal(false);
    navigate("/login", {
      state: {
        returnTo: window.location.pathname,
        articleId: article?.id,
      },
    });
  };

  if (loading) {
    return <LoadingSpinner fullscreen />;
  }

  if (error || !article) {
    return (
      <div className="not-found">
        <h2>Article Not Found</h2>
        <p>{error || "The article may have been removed."}</p>
        <button onClick={() => navigate("/")}>Return to Home</button>
      </div>
    );
  }

  const articleDate = article.date || article.createdAt || article.publishedAt;
  const formattedDate = articleDate
    ? format(new Date(articleDate), "MMMM d, yyyy")
    : "Date not available";
  const relatedArticles = getRelatedArticles();
  const tagsArray = Array.isArray(article.tags)
    ? article.tags
    : typeof article.tags === 'string'
      ? article.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      : [];

  return (
    <>
      {showLoginPopup && (
        <div className="auth-popup-container">
          {authMode === "login" ? (
            <Login
              isPopup={true}
              onClose={() => setShowLoginPopup(false)}
              onSwitchToSignup={() => setAuthMode("signup")}
            />
          ) : (
            <Signup
              isPopup={true}
              onClose={() => setShowLoginPopup(false)}
              onSwitchToLogin={() => setAuthMode("login")}
            />
          )}
        </div>
      )}
      <div className="adresolutionkaga">
        <div className="article-detail news-article">
          <div className="article-featured-image">
            <img
              src={article.imageUrl || "/images/icon.png"}
              alt={article.title}
              onError={(e) => {
                e.target.src = "/images/icon.png";
                e.target.alt = "Default news image";
              }}
            />
            {article.imageCaption && (
              <figcaption className="image-caption">
                {article.imageCaption}
              </figcaption>
            )}
          </div>
          <header className="article-header">
            <AnimatePresence>
              {showShareOptions && (
                <motion.div
                  className="share-optionsnewsart"
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="share-options-contentnews">
                    <div
                      onClick={(e) => shareOnSocialMedia("facebook", e)}
                      className="share-optionnews"
                      title="Share on Facebook"
                    >
                      <FaFacebook className="share-icon facebook" />
                      <span>Facebook</span>
                    </div>
                    <div
                      onClick={(e) => shareOnSocialMedia("whatsapp", e)}
                      className="share-optionnews"
                      title="Share on WhatsApp"
                    >
                      <FaWhatsapp className="share-icon whatsapp" />
                      <span>WhatsApp</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <h1 className="article-title">{article.title}</h1>
            <div className="article-meta">
              <span className="publication-date">{formattedDate}</span>
              {article.author && article.author !== "Admin" && (
                <span className="article-author">By {article.author}</span>
              )}
              {article.trending && (
                <span className="trending-badge">🔥 Trending</span>
              )}
            </div>
            {article.summary && (
              <div className="article-summary">
                <p>{article.summary}</p>
              </div>
            )}
            <div className="article-category">
              {article.category}
              <div className="cmtandshare">
                <button
                  className="like-button"
                  onClick={handleLike}
                  disabled={loadingLike}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "22px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <motion.span
                    key={liked}
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1.2 }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 10,
                    }}
                  >
                    {liked ? (
                      <ThumbsUp style={{ color: "#0d6efd" }} />
                    ) : (
                      <ThumbsUp style={{ color: "gray" }} />
                    )}
                  </motion.span>
                  <span>{likesCount}</span>
                </button>
                <button className="share-buttonnews" onClick={toggleShareOptions}>
                  <IoIosShareAlt />
                </button>
                <button
                  className="start-comment-buttontop"
                  onClick={() => {
                    const storedUser = JSON.parse(localStorage.getItem("user"));
                    if (storedUser) {
                      setShowCommentForm(true);
                      commentSectionRef.current?.scrollIntoView({
                        behavior: "smooth",
                      });
                    } else {
                      setShowLoginModal(true);
                      setAuthMode("choice");
                    }
                  }}
                >
                  <FaComment />
                </button>
              </div>
            </div>
          </header>
          <article className="maincentrearticle">
            <div className="article-body">
              <div className="article-content">{renderArticleContent()}</div>
            </div>
            {article.sourceUrl && (
              <div className="article-source">
                <h4>Source:</h4>
                <a
                  href={article.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="source-link"
                >
                  {new URL(article.sourceUrl).hostname}
                </a>
              </div>
            )}
            <div className="comments-section" ref={commentSectionRef}>
              <h2 className="section-title">
                Comments ({comments.length})
                {comments.length > 0 && (
                  <button
                    className="comments-toggle"
                    onClick={() => setShowComments(!showComments)}
                  >
                    {showComments ? "Hide" : "Show"}
                  </button>
                )}
              </h2>

              {!showCommentForm && (
                <button
                  className="start-comment-button"
                  id="callme"
                  onClick={() => {
                    const storedUser = JSON.parse(localStorage.getItem("user"));
                    if (storedUser) {
                      setShowCommentForm(true);
                    } else {
                      setShowLoginModal(true);
                      setAuthMode("choice");
                    }
                  }}
                >
                  <FaComment /> Add a Comment
                </button>
              )}

              {showCommentForm && (
                <form onSubmit={handleCommentSubmit} className="comment-form">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your thoughts..."
                    rows="3"
                    disabled={commentLoading}
                    required
                  />
                  <div className="comment-form-buttons">
                    <button
                      type="submit"
                      disabled={commentLoading || !newComment.trim()}
                    >
                      {commentLoading ? "Posting..." : "Post Comment"}
                    </button>
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={() => setShowCommentForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              <div
                className={`comments-list ${showComments ? "expanded" : ""}`}
              >
                {commentLoading ? (
                  <LoadingSpinner />
                ) : comments.length > 0 ? (
                  comments.map((comment) => (
                    <div key={comment.id} className="comment">
                      <div className="comment-header">
                        <span className="comment-author">{comment.comment_name}</span>
                        <span className="comment-date">
                          {format(
                            new Date(comment.created_at),
                            "MMM d, yyyy h:mm a"
                          )}
                        </span>
                        <button
                          className="delete-comment-btn"
                          onClick={() => handleDeleteComment(comment.id)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#ff6b6b",
                            cursor: "pointer",
                            fontSize: "12px",
                            marginLeft: "10px"
                          }}
                        >
                          Delete
                        </button>
                      </div>
                      <div className="comment-content">{comment.comment_text}</div>
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="replies-section">
                          {comment.replies.map((reply) => (
                            <div key={reply.id} className="reply">
                              <div className="reply-header">
                                <span className="reply-author">{reply.reply_name}</span>
                                <span className="reply-date">
                                  {format(new Date(reply.created_at), "MMM d, yyyy h:mm a")}
                                </span>
                              </div>
                              <div className="reply-content">{reply.reply_text}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const replyText = e.target.replyText.value;
                          if (replyText.trim()) {
                            handleReplySubmit(comment.id, replyText);
                            e.target.reset();
                          }
                        }}
                        className="reply-form"
                      >
                        <input
                          type="text"
                          name="replyText"
                          placeholder="Write a reply..."
                          required
                        />
                        <button type="submit">Reply</button>
                      </form>
                    </div>
                  ))
                ) : (
                  <p className="no-comments">No comments yet. Be the first to comment!</p>
                )}
              </div>
            </div>
            {tagsArray.length > 0 && (
              <div className="article-tags">
                <h4>Related Topics:</h4>
                <div className="tags-container">
                  {tagsArray.map((tag, index) => (
                    <span
                      key={index}
                      className="tag-item"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </article>
          <div className="latest-news-sidebar">
            <h3>Latest News</h3>
            <ul className="latest-news-list">
              {latestNews.length > 0 ? (
                latestNews.map((articleItem) => (
                  <li key={articleItem.id || `article-${Math.random()}`}>
                    <a
                      href={`/article/${articleItem.id}`}
                      className="latest-news-item"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/article/${articleItem.id}`, {
                          state: { article: articleItem, category: articleItem.category }
                        });
                      }}
                    >
                      <span className="latest-news-title">{articleItem.title}</span>
                      <span className="latest-news-time">
                        {getTimeAgo(articleItem.date || articleItem.createdAt)}
                      </span>
                    </a>
                  </li>
                ))
              ) : (
                <li className="no-news">No latest news available</li>
              )}
            </ul>
          </div>
          {loadingRelated ? (
            <LoadingSpinner />
          ) : relatedArticles.length > 0 ? (
            <div className="related-articles-section">
              <h2 className="section-title">
                More from {article.category}
              </h2>
              <div className="related-articles-list">
                {relatedArticles.map((relatedArticle) => (
                  <RelatedArticleItem
                    key={relatedArticle.id}
                    article={relatedArticle}
                    currentCategory={article.category}
                    currentArticleId={article.id}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <AnimatePresence>
        {showLoginModal && (
          <>
            <motion.div
              className="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-end",
                zIndex: 1000,
              }}
              onClick={() => setShowLoginModal(false)}
            >
              <motion.div
                className="modal-content"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                style={{
                  backgroundColor: "#1e293b",
                  padding: "20px",
                  borderTopLeftRadius: "6px",
                  borderTopRightRadius: "6px",
                  width: "100%",
                  maxWidth: "500px",
                  position: "relative",
                  maxHeight: "80vh",
                  overflowY: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowLoginModal(false)}
                  style={{
                    position: "absolute",
                    top: "15px",
                    right: "15px",
                    background: "none",
                    border: "none",
                    fontSize: "20px",
                    cursor: "pointer",
                    color: "white",
                  }}
                >
                  <FaTimes />
                </button>
                <h3
                  style={{
                    marginBottom: "15px",
                    textAlign: "center",
                    color: "white",
                    fontSize: "1.2rem",
                  }}
                >
                  Login to proceed
                </h3>
                {error && (
                  <div
                    className="error-message"
                    style={{
                      color: "#ff6b6b",
                      marginBottom: "15px",
                      textAlign: "center",
                      padding: "8px",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  >
                    {error}
                  </div>
                )}
                <div className="oauth-section">
                  <button
                    onClick={handleEmailLogin}
                    style={{
                      width: "100%",
                      padding: "12px",
                      color: "rgb(5, 65, 69)",
                      backgroundColor: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "500",
                      marginBottom: "10px",
                    }}
                  >
                    Use Email & Password
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default ArticleDetail;